import { X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '@/components/atoms/NewPcButton';
import Dropzone from '@/components/molecules/Dropzone';
import { cn } from '@/utils/tailwind';
import { useForm } from 'react-hook-form';

import { CPLAT_API_ROUTES, UploadSystem, usePost } from '@/api';
import { useLog } from '@/hooks/V3/useLog';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import TextField from '@/components/molecules/TextField';
import { CplatApiResponse } from '@/types/api';
import Input from '@/components/V3/Input';

type LabelProps = React.InputHTMLAttributes<HTMLDivElement> & {
  label: string;
  children: React.ReactNode;
  required?: boolean;
};

type FormData = {
  title: string;
  context: string;
  attachments: File[];
};

const InputWithLabel = ({ label, required, children, className, ...props }: LabelProps) => (
  <div
    className={cn('flex items-center space-x-[30px] text-gr-800 Body1S16', className)}
    {...props}
  >
    <div className="flex w-[100px] items-center self-start pt-[7px]">
      <p>{label}</p>
      {required && <span className="ml-[2px] text-red-500 Title3S18">*</span>}
    </div>
    {children}
  </div>
);

const renderFileNames = (attachments: File[], handleRemoveAttachment: (name: string) => void) => {
  return attachments.map((file, index) => (
    <div key={index} className="relative flex items-center gap-2 rounded-[4px] bg-gr-100 px-3 py-2">
      <span className="max-w-[120px] truncate text-gr-800 Body5M14">{file.name}</span>
      <X
        onClick={() => handleRemoveAttachment(file.name)}
        className="cursor-pointer text-gr-400 hover:text-point-500"
        size={16}
      />
    </div>
  ));
};

const InquiryContainer = ({ setSelectHistory }) => {
  const user = useAppSelector((state) => state.user);
  const { setUserEventHistory } = useLog(user.UserNo);
  const { register, watch, setValue, formState } = useForm<FormData>({
    defaultValues: {
      title: '',
      context: '',
      attachments: [],
    },
  });
  const navigate = useNavigate();

  const { mutate: inquiry, isPending } = usePost(CPLAT_API_ROUTES.inquiry);
  const [isImgUploading, setIsImgUploading] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const { showToast } = useToast();

  const formValues = watch();
  const { context, attachments, title } = formValues;

  const handleDrop = (newFiles: File[]) => {
    const updatedFiles = newFiles.filter(
      (newFile) => !attachments.some((existingFile) => existingFile.name === newFile.name),
    );

    // 30MB 초과시 업로드 불가
    const isOverSize = newFiles.reduce((curr, file) => (curr += file.size), 0) >= 30 * 1024 * 1024;

    if (isOverSize) {
      showToast('warning', '첨부파일은 최대 30MB까지 업로드할 수 있습니다.');
      return;
    }

    // attachments 배열에 새로운 파일을 추가
    setValue('attachments', [...attachments, ...updatedFiles]);
  };

  const handleRemoveAttachment = (fileName: string) => {
    setValue(
      'attachments',
      attachments.filter((attachment) => attachment.name !== fileName),
    );
  };

  const handleInquiry = async (e: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    const urls: string[] = [];
    for (const attachment of attachments) {
      setIsImgUploading(true);
      const { status, data } = await UploadSystem.uploadImgAsync(attachment);
      const { data: cplatData } = data as CplatApiResponse<{ ImageUrl: string }[]>;

      if (status == 200) {
        urls.push(cplatData[0].ImageUrl);
      } else {
        // toast({
        //   title: '이미지 업로드 중 오류가 발생하였습니다.',
        //   variant: 'error',
        // });
        showToast('warning', '이미지 업로드 중 오류가 발생하였습니다.');
      }
    }

    setIsImgUploading(false);

    const body = {
      cplatToken: user.CplatToken,
      title,
      context,
      attachments: urls.join(','),
      serviceType: '셀러박스',
    };

    inquiry(body, {
      onSuccess() {
        showToast('success', '문의가 등록되었습니다.');
        setSelectHistory(true);
        setUserEventHistory({
          eventName: 'register_personal_requiry',
          eventValue: body.title,
        });
      },
      onError(err) {
        console.error('문의 등록 실패:', err);
        showToast('warning', '문의 등록에 실패했습니다. 다시 시도해주세요.');
      },
    });
  };

  const handleCloseDialog = () => {
    showToast('success', '문의가 등록되었습니다.');
    setSelectHistory(true);
  };

  return (
    <>
      <form className="space-y-[16px] whitespace-nowrap" onSubmit={handleInquiry}>
        <InputWithLabel label="제목" required className="text-gr-800 Body2M16 ">
          <Input
            {...register('title', {
              required: true,
            })}
            maxLength={30}
            placeholder="제목을 입력해 주세요."
            className="border   focus:border-gr-400 "
          />
        </InputWithLabel>
        <InputWithLabel label="내용" required className="text-gr-800 Body2M16">
          <div className="relative w-full">
            <textarea
              {...register('context', {
                required: true,
              })}
              maxLength={500}
              placeholder="내용을 500자 이내로 작성해 주세요."
              className="bg-background flex h-[200px] w-full resize-none rounded-[8px] border border-gr-300  p-[10px] text-sm text-gr-800 placeholder-gr-400 Body2M16  focus:border-gr-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="absolute bottom-0 right-0 p-[10px] font-bold text-gr-700 Caption2S12">
              {context.length}/500
            </p>
          </div>
        </InputWithLabel>
        <InputWithLabel label="파일">
          <Dropzone
            height="40px"
            onDrop={handleDrop}
            acceptedFileTypes={['image/jpeg', 'image/jpg', 'image/png']}
          />
        </InputWithLabel>
        <div className="scrollbar ml-[120px] flex space-x-3 overflow-auto">
          {renderFileNames(attachments, handleRemoveAttachment)}
        </div>
        <div className="flex justify-center">
          <Button type="submit" className="h-[44px] w-[200px]" disabled={!formState.isValid}>
            {isPending || isImgUploading ? '문의 등록 중...' : '등록하기'}
          </Button>
        </div>
      </form>
      {/* <Dialog open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
        <DialogContent className="flex !w-[380px] flex-col items-center p-[30px] text-center">
          <DialogTitle>문의 등록이 완료되었어요</DialogTitle>
          <DialogDescription>
            답변이 완료되면 셀러박스 앱에서
            <br />
            푸시알림으로 안내드려요.
          </DialogDescription>
          <Button variant="secondary" className="w-[200px]" onClick={handleCloseDialog}>
            확인
          </Button>
        </DialogContent>
      </Dialog> */}
    </>
  );
};

export default InquiryContainer;
